import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Link,
  NoticeBox,
  Radio,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  CreateDatevLaudsEmployeeExportDocument,
  ExportEmployeesModalPageDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import { DATEV_LAUDS_INTEGRATION_ID } from 'modules/marketplace/lib/integrationIds'

interface Props {
  onClose: () => void
}

const ExportEmployeesModalPage = ({ onClose }: Props) => {
  const [exportType, setExportType] = useState('')
  const { company } = useCurrentSession()
  const { data: exporterData, loading } = useQuery(ExportEmployeesModalPageDocument, {})
  const { data: companyInstallationsData } = useGetMarketplaceCompanyInstallations(
    {
      companyIds: [company.id],
    },
    false
  )

  const canExportEmployees = exporterData?.employees._policies.exportEmployees.can ?? false

  const [
    createIntegrationsdatevlaudsEmployeeExport,
    { loading: creatingDatevLaudsEmployeeExport },
  ] = useMutation(CreateDatevLaudsEmployeeExportDocument, {
    extractorFn: (data) =>
      data.integrationsDatevLaudsMutations.createIntegrationsdatevlaudsEmployeeExport,
  })

  const {
    company: { subscription, id: companyId },
    access: { id: accessId },
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const hasDatevIntegration = companyInstallationsData?.some(
    (installation) => installation.integrationDatoId === DATEV_LAUDS_INTEGRATION_ID
  )

  const onSubmit = async () => {
    if (!exportType || !canExportEmployees) return
    if (exportType === 'xlsx') {
      return navigate(navigation.employees.list.export.fullPath())
    }

    const result = await createIntegrationsdatevlaudsEmployeeExport({
      variables: {
        companyId: companyId,
        accessId: accessId,
      },
    })
    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('employees.export_selection.success_message'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.export_selection.error_message'),
      })
    }

    onClose()
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="download"
        title={i18n.t('employees.export_selection.title')}
        description={i18n.t('employees.export_selection.description')}
        onClose={onClose}
        onSubmit={onSubmit}
        footer={
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={!exportType || loading || creatingDatevLaudsEmployeeExport} submit>
              {i18n.t('common.confirm')}
            </Button>
          </>
        }
      >
        {loading && <Spinner />}
        {!loading && (
          <>
            {hasDatevIntegration && (
              <Radio.Wrap>
                <Radio
                  name={i18n.t('employees.export_selection.options.excel.title')}
                  value={exportType}
                  onChange={(value) => setExportType(value)}
                  description={i18n.t('employees.export_selection.options.excel.description')}
                  option={{
                    label: i18n.t('employees.export_selection.options.excel.title'),
                    value: 'xlsx',
                  }}
                ></Radio>
              </Radio.Wrap>
            )}

            <Radio.Wrap>
              <Radio
                name={i18n.t('employees.export_selection.options.datev.title')}
                value={exportType}
                onChange={(value) => setExportType(value)}
                description={i18n.t('employees.export_selection.options.datev.description')}
                option={{
                  label: i18n.t('employees.export_selection.options.datev.title'),
                  value: 'datev',
                }}
              ></Radio>
            </Radio.Wrap>
            {exportType === 'datev' && (
              <NoticeBox
                paddingY="s24"
                color="viridian100"
                message={
                  <Box gap="s8">
                    <Text.Normal>{i18n.t('employees.export_selection.hint')}</Text.Normal>
                    <Link.Touchable
                      onClick={() =>
                        navigate(
                          navigation.discover.marketplace.item.settings.fullPath({
                            id: DATEV_LAUDS_INTEGRATION_ID,
                          })
                        )
                      }
                    >
                      {i18n.t('employees.export_selection.hint_link_text')}
                    </Link.Touchable>
                  </Box>
                }
                fullWidth
              />
            )}
          </>
        )}
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(ExportEmployeesModalPage)
